import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/** One `Label: saved → current` line, with an optional note underneath. */
const DriftRow = ({ item }) => (
  <Box component="li" sx={{ mb: 0.5 }}>
    <Typography component="span" sx={{ fontSize: 13, fontWeight: 700 }}>
      {item.label}:
    </Typography>{" "}
    <Typography component="span" sx={{ fontSize: 13 }}>
      {item.saved} → {item.current}
    </Typography>
    {item.note && (
      <Typography sx={{ fontSize: 11.5, color: "#667085" }}>{item.note}</Typography>
    )}
  </Box>
);

/**
 * Tells the operator which master values have changed since this policy was
 * saved. The saved values are kept; the button opts in to the latest ones.
 *
 * @param {Array}    items       rows from computeMasterDrift — nothing renders when empty
 * @param {Function} onApply     called after the operator confirms
 * @param {boolean}  premiumKept the saved premium is on screen (no recalculation happened)
 */
const MasterDriftNotice = ({ items = [], onApply, premiumKept = false }) => {
  const [open, setOpen] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!items.length) return null;

  const applicable = items.filter((i) => i.applicable !== false);
  const informational = items.filter((i) => i.applicable === false);

  return (
    <Box sx={{ px: "5px", pt: 2 }}>
      <Alert severity="info" sx={{ alignItems: "flex-start" }}>
        <AlertTitle sx={{ fontWeight: 700 }}>
          Master data has changed since this policy was saved — saved values are kept
        </AlertTitle>

        <Button
          size="small"
          onClick={() => setOpen((v) => !v)}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
        >
          {open ? "Hide" : "Show"} {items.length} change{items.length > 1 ? "s" : ""}
        </Button>

        <Collapse in={open}>
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {items.map((item) => (
              <DriftRow key={item.key} item={item} />
            ))}
          </Box>
        </Collapse>

        {premiumKept && (
          <Typography sx={{ fontSize: 12, color: "#475467", mt: 1 }}>
            The saved premium is kept as well — it is recalculated only if you change a
            pricing field or use the latest master values.
          </Typography>
        )}

        <Button
          size="small"
          variant="outlined"
          disabled={applicable.length === 0}
          onClick={() => setConfirmOpen(true)}
          sx={{ textTransform: "none", mt: 1.5 }}
        >
          Use latest master values
        </Button>
      </Alert>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Use the latest master values?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14, mb: 1.5 }}>
            These saved values will be replaced with the current master values. The premium
            will need to be recalculated before you can continue.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {applicable.map((item) => (
              <DriftRow key={item.key} item={item} />
            ))}
          </Box>
          {informational.length > 0 && (
            <Typography sx={{ fontSize: 12, color: "#667085", mt: 1.5 }}>
              Not changed: {informational.map((i) => i.label).join(", ")}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              if (onApply) onApply();
            }}
            sx={{ textTransform: "none" }}
          >
            Use latest values
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterDriftNotice;
